import { useState, useCallback, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { crmService } from '@/services/crm/crmService';
import { useTranslation } from '@/store/languageStore';
import { toast } from 'react-hot-toast';

interface CompanySearchResult {
  organisationsnummer: string;
  foretagsnamn: string;
  adress?: string;
  postnummer?: string;
  postort?: string;
  telefon?: string;
  bolagsform?: string;
  status?: string;
  co?: string;
}

interface CompanySearchModalProps {
  onSelect: (company: CompanySearchResult) => void;
  onClose: () => void;
}

const PAGE_SIZE = 25;

export default function CompanySearchModal({ onSelect, onClose }: CompanySearchModalProps) {
  const { t } = useTranslation();
  const [who, setWho] = useState('');
  const [where, setWhere] = useState('');
  const [results, setResults] = useState<CompanySearchResult[]>([]);
  const [totalFound, setTotalFound] = useState(0);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(results.length / PAGE_SIZE)), [results.length]);
  const pagedResults = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return results.slice(start, start + PAGE_SIZE);
  }, [results, currentPage]);

  const handleSearch = useCallback(async () => {
    if (who.trim().length < 2 && where.trim().length < 2) {
      toast.error(t('companySearch.minChars'));
      return;
    }
    try {
      setSearching(true);
      setHasSearched(true);
      setCurrentPage(1);
      const data = await crmService.searchCompanies(who.trim(), where.trim() || undefined);
      setResults(data.results);
      setTotalFound(data.totalFound);
    } catch {
      toast.error(t('companySearch.searchError'));
    } finally {
      setSearching(false);
    }
  }, [who, where, t]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  }, [handleSearch]);

  const bolagsformLabel = (code: string): string => {
    const map: Record<string, string> = {
      LimitedCompany: 'AB',
      SoleProprietorship: 'Enskild firma',
      TradingPartnership: 'HB',
      LimitedPartnership: 'KB',
      EconomicAssociation: 'Ekonomisk förening',
      NonProfitAssociation: 'Ideell förening',
      Foundation: 'Stiftelse',
    };
    return map[code] || code;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-bgElevated rounded-xl border border-border w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-textDefault flex items-center gap-2">
            <MagnifyingGlassIcon className="w-5 h-5" />
            {t('companySearch.title')}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-bgHover rounded-lg">
            <XMarkIcon className="w-5 h-5 text-textSecondary" />
          </button>
        </div>

        {/* Search fields */}
        <div className="p-5 border-b border-border">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm text-textSecondary mb-1">
                {t('companySearch.whoLabel')}
              </label>
              <input
                type="text"
                value={who}
                onChange={(e) => setWho(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('companySearch.whoPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault focus:ring-2 focus:ring-bgPrimary focus:border-bgPrimary"
                autoFocus
              />
            </div>
            <div className="w-44">
              <label className="block text-sm text-textSecondary mb-1">
                {t('companySearch.whereLabel')}
              </label>
              <input
                type="text"
                value={where}
                onChange={(e) => setWhere(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('companySearch.wherePlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault focus:ring-2 focus:ring-bgPrimary focus:border-bgPrimary"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={searching || (who.trim().length < 2 && where.trim().length < 2)}
                className="px-4 py-2 bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
                {searching ? t('companySearch.searching') : t('companySearch.search')}
              </button>
            </div>
          </div>
          <p className="text-xs text-textTertiary mt-2">
            {t('companySearch.wildcardHint')}
          </p>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-5">
          {searching && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bgPrimary" />
            </div>
          )}

          {!searching && hasSearched && results.length === 0 && (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-3 text-textTertiary opacity-50" />
              <p className="text-textSecondary">{t('companySearch.noResults')}</p>
            </div>
          )}

          {!searching && results.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-textSecondary">
                  {totalFound > results.length
                    ? t('companySearch.showingOfTotal', { showing: results.length, total: totalFound })
                    : t('companySearch.resultsCount', { count: results.length })}
                </p>
                {totalFound > results.length && (
                  <p className="text-xs text-amber-600">
                    {t('companySearch.refineSearch')}
                  </p>
                )}
              </div>

              {pagedResults.map((company, idx) => (
                <button
                  key={`${company.organisationsnummer}-${idx}`}
                  onClick={() => onSelect(company)}
                  className="w-full text-left p-4 rounded-lg border border-border bg-bgBase hover:bg-bgHover hover:border-bgPrimary/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-textDefault flex items-center gap-2">
                        <BuildingOfficeIcon className="w-4 h-4 text-bgPrimary flex-shrink-0" />
                        {company.foretagsnamn}
                      </div>
                      <div className="text-sm text-textSecondary mt-1">
                        Org.nr: {company.organisationsnummer}
                        {company.bolagsform && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-bgContainer rounded">
                            {bolagsformLabel(company.bolagsform)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-textTertiary">
                        {(company.adress || company.postort) && (
                          <span className="flex items-center gap-1">
                            <MapPinIcon className="w-3.5 h-3.5" />
                            {[company.adress, company.postnummer, company.postort]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        )}
                        {company.telefon && (
                          <span className="flex items-center gap-1">
                            <PhoneIcon className="w-3.5 h-3.5" />
                            {company.telefon}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!searching && !hasSearched && (
            <div className="text-center py-12">
              <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-3 text-textTertiary opacity-50" />
              <p className="text-textSecondary">{t('companySearch.startSearching')}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!searching && results.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border">
            <p className="text-sm text-textSecondary">
              {t('companySearch.page')} {currentPage} / {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="p-1.5 rounded-lg border border-border hover:bg-bgHover disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-lg border border-border hover:bg-bgHover disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
